import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, Button, Alert, Spinner, Form, Row, Col, ListGroup, Tab, Nav, Badge, Modal, ProgressBar } from 'react-bootstrap';
import SurveyForm from '../components/SurveyForm';
import { 
  getSession, 
  submitAnswers, 
  getAttachments, 
  processAttachments, 
  processSingleAttachment, 
  uploadAttachment, 
  getAttachmentDetails,
  getTaskStatus,
  cancelTask,
  abandonTask,
  getAttachmentTasks,
  retryAttachmentTask,
  getStaleTasks,
  fillAnswersFromAttachment
} from '../services/api';

const SessionDetail = () => {
  const { sessionId } = useParams();
  const [session, setSession] = useState(null);
  const [survey, setSurvey] = useState(null);
  const [answers, setAnswers] = useState({});
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [processingAttachments, setProcessingAttachments] = useState(false);
  const [showTranscriptionModal, setShowTranscriptionModal] = useState(false);
  const [currentTranscription, setCurrentTranscription] = useState('');
  const [currentAttachmentId, setCurrentAttachmentId] = useState(null);
  
  // Task tracking
  const [activeTasks, setActiveTasks] = useState({});  // { taskId: taskDetails }
  const [taskPollingInterval, setTaskPollingInterval] = useState(null);
  const [showCancelConfirmModal, setShowCancelConfirmModal] = useState(false);
  const [showAbandonConfirmModal, setShowAbandonConfirmModal] = useState(false);
  const [taskToCancel, setTaskToCancel] = useState(null);
  const [taskToAbandon, setTaskToAbandon] = useState(null);
  const [staleTasks, setStaleTasks] = useState([]);
  const [exportingPdf, setExportingPdf] = useState(false);

  // Load session data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Check if we have any locally abandoned attachments
        const abandonedAttachments = JSON.parse(localStorage.getItem('abandonedAttachments') || '[]');
        
        const sessionData = await getSession(sessionId);
        setSession(sessionData);
        
        // Extract survey data
        setSurvey(sessionData.survey);
        
        // Initialize answers from existing data
        const initialAnswers = {};
        if (sessionData.answers) {
          sessionData.answers.forEach(answer => {
            initialAnswers[answer.question_id] = answer.value;
          });
        }
        setAnswers(initialAnswers);
        
        // Load attachments
        const attachmentsData = await getAttachments(sessionId);
        
        // Check if any of our attachments were locally abandoned before
        if (abandonedAttachments.length > 0) {
          // Filter to find attachments that are still in 'processing' state but were locally abandoned
          const previouslyAbandoned = attachmentsData.filter(att => 
            att.status === 'processing' && abandonedAttachments.includes(att.id)
          );
          
          // If we found any, ask the user if they want to mark them as failed
          if (previouslyAbandoned.length > 0) {
            const shouldUpdate = window.confirm(
              `Found ${previouslyAbandoned.length} attachment(s) that were previously abandoned locally. ` +
              `Would you like to mark them as failed?`
            );
            
            if (shouldUpdate) {
              // Mark abandoned attachments as failed
              const updatedAttachments = attachmentsData.map(att => 
                abandonedAttachments.includes(att.id) && att.status === 'processing' 
                  ? { ...att, status: 'failed' } 
                  : att
              );
              setAttachments(updatedAttachments);
              
              // Continue with the rest of the processing
              setSuccessMessage(`Marked ${previouslyAbandoned.length} previously abandoned attachment(s) as failed.`);
              setTimeout(() => {
                setSuccessMessage(null);
              }, 5000);
            } else {
              // User chose not to update, so clear the local storage
              localStorage.removeItem('abandonedAttachments');
              setAttachments(attachmentsData);
            }
          } else {
            // If none of the abandoned attachments are still in processing state,
            // we can clear the local storage
            localStorage.removeItem('abandonedAttachments');
            setAttachments(attachmentsData);
          }
        } else {
          setAttachments(attachmentsData);
        }
        
        // Load active tasks for this session
        await loadActiveTasks();
        
        // Start a backend connectivity check timer
        startConnectivityCheck();
        
        setError(null);
      } catch (err) {
        console.error('Error fetching session details:', err);
        setError('Failed to load session. Please try again later.');
        
        // Check if this is a network error or backend down
        const isNetworkError = err.message.includes('Network Error') || 
                               err.message.includes('Failed to fetch') ||
                               err.message.includes('Connection refused');
                               
        if (isNetworkError) {
          setError('Backend server appears to be down. Some functionality will be limited.');
          
          // Try to recover any locally saved data
          // This would typically come from local storage in a more complete implementation
          // For now, we'll just provide a warning message
          setSuccessMessage('Working in offline mode. Some actions may not be available.');
          setTimeout(() => {
            setSuccessMessage(null);
          }, 5000);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    
    // Clean up polling interval when component unmounts
    return () => {
      if (taskPollingInterval) {
        clearInterval(taskPollingInterval);
      }
      if (connectivityCheckInterval) {
        clearInterval(connectivityCheckInterval);
      }
    };
  }, [sessionId]);
  
  // Backend connectivity check
  const [backendOnline, setBackendOnline] = useState(true);
  const [connectivityCheckInterval, setConnectivityCheckInterval] = useState(null);
  
  const startConnectivityCheck = () => {
    // Clear any existing interval
    if (connectivityCheckInterval) {
      clearInterval(connectivityCheckInterval);
    }
    
    // Create a new interval that checks backend connectivity every 30 seconds
    const interval = setInterval(async () => {
      try {
        // Make a simple request to the backend to check if it's online
        // We use a GET request to a lightweight endpoint
        await getAttachments(sessionId);
        
        // If the request succeeds, mark backend as online
        if (!backendOnline) {
          console.log('Backend connection restored');
          setBackendOnline(true);
          setSuccessMessage('Backend connection restored. Refreshing data...');
          
          // Reload all data since backend is back
          try {
            const refreshedAttachments = await getAttachments(sessionId);
            setAttachments(refreshedAttachments);
            await loadActiveTasks();
          } catch (refreshError) {
            console.error('Error refreshing data after backend reconnection:', refreshError);
          }
          
          setTimeout(() => {
            setSuccessMessage(null);
          }, 3000);
        }
      } catch (err) {
        // If the request fails, mark backend as offline
        if (backendOnline) {
          console.log('Backend connection lost');
          setBackendOnline(false);
          setError('Connection to backend lost. Some functionality will be limited.');
          setTimeout(() => {
            setError(null);
          }, 5000);
        }
      }
    }, 30000); // Check every 30 seconds
    
    setConnectivityCheckInterval(interval);
  };
  
  // Load active tasks for this session
  const loadActiveTasks = async () => {
    try {
      // Get all tasks for attachments in this session
      const tasks = {};
      
      // First check if we have attachment IDs
      if (attachments && attachments.length > 0) {
        for (const attachment of attachments) {
          const attachmentTasks = await getAttachmentTasks(attachment.id);
          
          // Add active tasks to our tracker
          if (attachmentTasks.count > 0) {
            attachmentTasks.tasks.forEach(task => {
              // Only track non-completed tasks
              if (!task.complete && !task.cancelled) {
                tasks[task.task_id] = {
                  ...task,
                  attachmentId: attachment.id
                };
              }
            });
          }
        }
      }
      
      // Update state with active tasks
      setActiveTasks(tasks);
      
      // Set up polling for task status updates if we have active tasks
      if (Object.keys(tasks).length > 0) {
        startTaskPolling();
      }
    } catch (err) {
      console.error('Error loading active tasks:', err);
    }
  };
  
  // Start polling for task status
  const startTaskPolling = () => {
    // Clear existing interval if any
    if (taskPollingInterval) {
      clearInterval(taskPollingInterval);
    }
    
    // Set up new polling interval (every 2 seconds)
    const interval = setInterval(pollTaskStatus, 2000);
    setTaskPollingInterval(interval);
  };
  
  // Poll for status of all active tasks
  const pollTaskStatus = async () => {
    const taskIds = Object.keys(activeTasks);
    if (taskIds.length === 0) {
      // No active tasks, stop polling
      if (taskPollingInterval) {
        clearInterval(taskPollingInterval);
        setTaskPollingInterval(null);
      }
      return;
    }
    
    // Update status for each active task
    const updatedTasks = { ...activeTasks };
    let tasksChanged = false;
    let completedTasks = [];
    let staleTasksFound = [];
    
    for (const taskId of taskIds) {
      try {
        const status = await getTaskStatus(taskId);
        
        // Update task status
        updatedTasks[taskId] = {
          ...updatedTasks[taskId],
          ...status
        };
        
        // Check if task is complete
        if (status.complete) {
          completedTasks.push(taskId);
          tasksChanged = true;
          
          // Update UI since task completed
          if (status.success) {
            console.log(`Task ${taskId} completed successfully`);
            // Reload attachments to reflect changes
            const refreshedAttachments = await getAttachments(sessionId);
            setAttachments(refreshedAttachments);
          }
        } else if (status.is_stale) {
          // Task is stale
          staleTasksFound.push(status);
          tasksChanged = true;
        } else if (status.progress !== updatedTasks[taskId].progress) {
          // Progress changed
          tasksChanged = true;
        }
      } catch (err) {
        console.error(`Error polling status for task ${taskId}:`, err);
      }
    }
    
    // Update state if tasks changed
    if (tasksChanged) {
      // Remove completed tasks
      completedTasks.forEach(taskId => {
        delete updatedTasks[taskId];
      });
      
      setActiveTasks(updatedTasks);
      
      // Update stale tasks list
      if (staleTasksFound.length > 0) {
        setStaleTasks(staleTasksFound);
      }
      
      // Stop polling if no more active tasks
      if (Object.keys(updatedTasks).length === 0) {
        clearInterval(taskPollingInterval);
        setTaskPollingInterval(null);
      }
    }
    
    // Every 5th poll (approx 10 seconds), check for stale tasks
    const currentTime = Date.now();
    const lastCheckTime = localStorage.getItem('lastStaleTaskCheck');
    
    if (!lastCheckTime || (currentTime - parseInt(lastCheckTime)) > 10000) {
      try {
        // Check for any stale tasks we might have missed
        const staleTasksResponse = await getStaleTasks(300); // 5 minutes timeout
        
        if (staleTasksResponse.stale_count > 0) {
          const newStaleTasks = staleTasksResponse.stale_tasks.filter(
            task => task.attachment_id && !completedTasks.includes(task.task_id)
          );
          
          if (newStaleTasks.length > 0) {
            setStaleTasks(currentStaleTasks => {
              // Merge with existing stale tasks, avoiding duplicates
              const existingIds = currentStaleTasks.map(t => t.task_id);
              const uniqueNewTasks = newStaleTasks.filter(t => !existingIds.includes(t.task_id));
              return [...currentStaleTasks, ...uniqueNewTasks];
            });
          }
        }
        
        // Update last check time
        localStorage.setItem('lastStaleTaskCheck', currentTime.toString());
      } catch (err) {
        console.error('Error checking for stale tasks:', err);
      }
    }
  };

  // Handle input changes
  const handleAnswerChange = (questionId, value, type) => {
    if (type === 'checkbox') {
      // For checkboxes, toggle the option in array
      const currentAnswers = answers[questionId] || [];
      let newAnswers;
      
      if (currentAnswers.includes(value)) {
        newAnswers = currentAnswers.filter(item => item !== value);
      } else {
        newAnswers = [...currentAnswers, value];
      }
      
      setAnswers({
        ...answers,
        [questionId]: newAnswers
      });
    } else {
      // For other types, just update the value
      setAnswers({
        ...answers,
        [questionId]: value
      });
    }
  };

  // Submit answers
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSubmitting(true);
      setError(null);
      
      // Format answers for API
      const formattedAnswers = Object.keys(answers).map(questionId => ({
        question_id: questionId,
        value: answers[questionId]
      }));
      
      await submitAnswers(sessionId, formattedAnswers);
      setSuccessMessage('Answers saved successfully!');
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err) {
      console.error('Error submitting answers:', err);
      setError('Failed to save answers. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Process all attachments
  const handleProcessAttachments = async () => {
    try {
      setProcessingAttachments(true);
      setError(null);
      
      await processAttachments(sessionId);
      
      // Reload attachments
      const updatedAttachments = await getAttachments(sessionId);
      setAttachments(updatedAttachments);
      
      // Reload session to get updated answers
      const updatedSession = await getSession(sessionId);
      setSession(updatedSession);
      
      // Initialize answers from updated data
      const updatedAnswers = {};
      if (updatedSession.answers) {
        updatedSession.answers.forEach(answer => {
          updatedAnswers[answer.question_id] = answer.value;
        });
      }
      setAnswers(updatedAnswers);
      
      setSuccessMessage('Attachments processed successfully!');
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
      
    } catch (err) {
      console.error('Error processing attachments:', err);
      setError('Failed to process attachments. Please try again.');
    } finally {
      setProcessingAttachments(false);
    }
  };

  // Process a single attachment
  const handleProcessAttachment = async (attachmentId) => {
    try {
      // Find the attachment and set its status to processing
      const updatedAttachments = attachments.map(att => 
        att.id === attachmentId ? { ...att, status: 'processing' } : att
      );
      setAttachments(updatedAttachments);
      
      await processSingleAttachment(attachmentId);
      
      // Reload attachments
      const refreshedAttachments = await getAttachments(sessionId);
      setAttachments(refreshedAttachments);
      
      // Reload session to get updated answers
      const updatedSession = await getSession(sessionId);
      setSession(updatedSession);
      
      // Initialize answers from updated data
      const updatedAnswers = {};
      if (updatedSession.answers) {
        updatedSession.answers.forEach(answer => {
          updatedAnswers[answer.question_id] = answer.value;
        });
      }
      setAnswers(updatedAnswers);
      
    } catch (err) {
      console.error(`Error processing attachment ${attachmentId}:`, err);
      setError(`Failed to process attachment. Please try again.`);
    }
  };
  
  // Force reprocess a transcription (even if it's already done)
  const handleReprocessTranscription = async (attachmentId) => {
    try {
      setError(null);
      
      // Find the attachment and set its status to processing
      const updatedAttachments = attachments.map(att => 
        att.id === attachmentId ? { ...att, status: 'processing' } : att
      );
      setAttachments(updatedAttachments);
      
      // Process with force=true to regenerate transcription
      const response = await processSingleAttachment(attachmentId, true);
      
      // Check if the response indicates async processing
      if (response.async && response.task_id) {
        console.log(`Transcription started asynchronously with task ID: ${response.task_id}`);
        
        // Add task to active tasks
        const newActiveTasks = { ...activeTasks };
        newActiveTasks[response.task_id] = {
          task_id: response.task_id,
          attachment_id: attachmentId,
          attachmentId: attachmentId, // For consistency with our data structure
          status: 'processing',
          progress: 0,
          complete: false,
          description: `Transcription of attachment ${attachmentId}`
        };
        
        setActiveTasks(newActiveTasks);
        
        // Start polling if not already polling
        if (!taskPollingInterval) {
          startTaskPolling();
        }
        
        setSuccessMessage('Transcription started in background. You can continue working while it processes.');
      } else {
        // Synchronous processing completed
        // Reload attachments
        const refreshedAttachments = await getAttachments(sessionId);
        setAttachments(refreshedAttachments);
        
        // Reload session to get updated answers
        const updatedSession = await getSession(sessionId);
        setSession(updatedSession);
        
        // Initialize answers from updated data
        const updatedAnswers = {};
        if (updatedSession.answers) {
          updatedSession.answers.forEach(answer => {
            updatedAnswers[answer.question_id] = answer.value;
          });
        }
        setAnswers(updatedAnswers);
        
        setSuccessMessage('Transcription reprocessed successfully!');
      }
      
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
      
    } catch (err) {
      console.error(`Error reprocessing transcription for attachment ${attachmentId}:`, err);
      
      // Prepare error message
      let errorMessage = 'Failed to reprocess transcription. Please try again.';
      let errorDetail = '';
      
      if (err.response && err.response.data) {
        console.log('Server response data:', err.response.data);
        
        // Try to get structured error message
        if (typeof err.response.data === 'object') {
          const responseError = err.response.data.error || '';
          
          if (responseError) {
            errorMessage = responseError;
            errorDetail = 'See console for more details.';
          } else if (err.response.data.message) {
            // Use server-provided message if available
            errorMessage = err.response.data.message;
          }
        } else if (typeof err.response.data === 'string') {
          // Raw error response
          errorMessage = 'Server error occurred';
          errorDetail = err.response.data.substring(0, 100) + '...';
        }
      } else if (err.message) {
        // Use client-side error message
        errorMessage = err.message;
        
        if (err.message.includes('transcription service')) {
          errorMessage = 'Could not connect to the transcription service. Please check the service is running and accessible.';
        } else if (err.message.includes('timeout')) {
          errorMessage = 'Connection to transcription service timed out. The audio file may be too large or the service is unavailable.';
        }
      }
      
      // Update the attachment status back to 'done' to reflect that processing failed
      const revertedAttachments = attachments.map(att => 
        att.id === attachmentId ? { ...att, status: 'failed' } : att
      );
      setAttachments(revertedAttachments);
      
      setError(errorDetail ? `${errorMessage} - ${errorDetail}` : errorMessage);
      
      // Refresh attachments list to get actual server status
      setTimeout(async () => {
        try {
          const refreshedAttachments = await getAttachments(sessionId);
          setAttachments(refreshedAttachments);
        } catch (refreshError) {
          console.error('Error refreshing attachments:', refreshError);
        }
      }, 2000);
    }
  };
  
  // Handle cancelling a task
  const handleCancelTask = async (taskId) => {
    try {
      // First show confirmation modal
      setTaskToCancel(taskId);
      setShowCancelConfirmModal(true);
    } catch (err) {
      console.error(`Error preparing to cancel task ${taskId}:`, err);
    }
  };
  
  // Confirm and execute task cancellation
  const confirmCancelTask = async () => {
    try {
      if (!taskToCancel) return;
      
      const taskId = taskToCancel;
      const response = await cancelTask(taskId);
      
      console.log(`Cancelled task ${taskId}:`, response);
      
      // Update task in state to reflect cancellation
      const updatedTasks = { ...activeTasks };
      
      if (updatedTasks[taskId]) {
        updatedTasks[taskId] = {
          ...updatedTasks[taskId],
          cancelled: true,
          complete: true
        };
      }
      
      setActiveTasks(updatedTasks);
      
      // Remove task from active tasks after a brief delay
      setTimeout(() => {
        const cleanedTasks = { ...updatedTasks };
        delete cleanedTasks[taskId];
        setActiveTasks(cleanedTasks);
        
        // Also remove from stale tasks if present
        setStaleTasks(currentStaleTasks => currentStaleTasks.filter(t => t.task_id !== taskId));
        
        // Reload attachments after cancellation
        getAttachments(sessionId).then(refreshedAttachments => {
          setAttachments(refreshedAttachments);
        });
      }, 1000);
      
      setSuccessMessage('Task cancelled successfully');
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err) {
      console.error(`Error cancelling task ${taskToCancel}:`, err);
      setError('Failed to cancel task. Please try again.');
    } finally {
      // Close modal and clear task
      setShowCancelConfirmModal(false);
      setTaskToCancel(null);
    }
  };
  
  // Handle abandoning a task
  const handleAbandonTask = async (taskId) => {
    try {
      // First show confirmation modal
      setTaskToAbandon(taskId);
      setShowAbandonConfirmModal(true);
    } catch (err) {
      console.error(`Error preparing to abandon task ${taskId}:`, err);
    }
  };
  
  // Confirm and execute task abandonment
  const confirmAbandonTask = async () => {
    try {
      if (!taskToAbandon) return;
      
      const taskId = taskToAbandon;
      
      try {
        // Try to contact the backend first
        const response = await abandonTask(taskId);
        console.log(`Abandoned task ${taskId}:`, response);
      } catch (backendError) {
        // If backend fails, we'll still proceed with local UI updates
        console.error(`Backend error while abandoning task ${taskId}:`, backendError);
        console.log('Proceeding with frontend-only abandonment');
      }
      
      // Always update the UI regardless of backend response
      const updatedTasks = { ...activeTasks };
      
      if (updatedTasks[taskId]) {
        delete updatedTasks[taskId];
      }
      
      setActiveTasks(updatedTasks);
      
      // Remove from stale tasks list
      setStaleTasks(currentStaleTasks => currentStaleTasks.filter(t => t.task_id !== taskId));
      
      // Try to reload attachments after abandonment, but don't fail if backend is down
      try {
        const refreshedAttachments = await getAttachments(sessionId);
        setAttachments(refreshedAttachments);
      } catch (reloadError) {
        console.error('Failed to reload attachments, backend may be down:', reloadError);
      }
      
      setSuccessMessage('Task abandoned successfully');
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err) {
      console.error(`Error abandoning task ${taskToAbandon}:`, err);
      setError('Failed to abandon task. Please try again.');
    } finally {
      // Close modal and clear task
      setShowAbandonConfirmModal(false);
      setTaskToAbandon(null);
    }
  };
  
  // Handle local abandonment when backend is unreachable
  const handleLocalAbandon = async (attachmentId) => {
    try {
      // Show confirmation dialog
      if (window.confirm('Backend may be unresponsive. Abandon this processing task locally?')) {
        // Update the attachment status directly in the UI
        const updatedAttachments = attachments.map(att => 
          att.id === attachmentId ? { ...att, status: 'failed' } : att
        );
        setAttachments(updatedAttachments);
        
        // Remove any matching active tasks from state
        const updatedTasks = { ...activeTasks };
        Object.keys(updatedTasks).forEach(taskId => {
          if (updatedTasks[taskId].attachment_id === attachmentId) {
            delete updatedTasks[taskId];
          }
        });
        setActiveTasks(updatedTasks);
        
        // Remove from stale tasks list
        setStaleTasks(currentStaleTasks => 
          currentStaleTasks.filter(t => t.attachment_id !== attachmentId)
        );
        
        setSuccessMessage('Processing abandoned locally. Refresh page when backend is available.');
        setTimeout(() => {
          setSuccessMessage(null);
        }, 5000);
        
        // Store in local storage that this attachment was locally abandoned
        const abandonedAttachments = JSON.parse(localStorage.getItem('abandonedAttachments') || '[]');
        if (!abandonedAttachments.includes(attachmentId)) {
          abandonedAttachments.push(attachmentId);
          localStorage.setItem('abandonedAttachments', JSON.stringify(abandonedAttachments));
        }
      }
    } catch (err) {
      console.error(`Error during local abandonment for attachment ${attachmentId}:`, err);
      setError('Failed to abandon task locally. Please try again.');
    }
  };
  
  // Handle retrying a transcription
  const handleRetryTranscription = async (attachmentId, force = false) => {
    try {
      setError(null);
      
      // Find the attachment and set its status to processing
      const updatedAttachments = attachments.map(att => 
        att.id === attachmentId ? { ...att, status: 'processing' } : att
      );
      setAttachments(updatedAttachments);
      
      // First, abandon any stale tasks for this attachment
      const attachmentTasks = await getAttachmentTasks(attachmentId);
      
      const staleTasks = attachmentTasks.tasks.filter(
        task => !task.complete && !task.cancelled && task.is_stale
      );
      
      // Abandon stale tasks
      for (const task of staleTasks) {
        try {
          console.log(`Abandoning stale task ${task.task_id} before retry`);
          await abandonTask(task.task_id);
        } catch (err) {
          console.error(`Error abandoning stale task ${task.task_id}:`, err);
        }
      }
      
      // Retry transcription
      const response = await retryAttachmentTask(attachmentId, force);
      console.log('Retry response:', response);
      
      if (response.new_task_id) {
        // Add new task to active tasks
        const newTaskId = response.new_task_id;
        const newTask = response.task;
        
        const updatedActiveTasks = { ...activeTasks };
        updatedActiveTasks[newTaskId] = {
          ...newTask,
          attachmentId // For consistency
        };
        
        setActiveTasks(updatedActiveTasks);
        
        // Start polling if not already polling
        if (!taskPollingInterval) {
          startTaskPolling();
        }
        
        setSuccessMessage('Transcription retry started in background');
      } else {
        // No new task was created - refresh the UI
        const refreshedAttachments = await getAttachments(sessionId);
        setAttachments(refreshedAttachments);
        
        setSuccessMessage(response.message || 'No changes made');
      }
      
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
      
    } catch (err) {
      console.error(`Error retrying transcription for attachment ${attachmentId}:`, err);
      setError('Failed to retry transcription. Please try again.');
      
      // Refresh attachments list to get actual server status
      setTimeout(async () => {
        try {
          const refreshedAttachments = await getAttachments(sessionId);
          setAttachments(refreshedAttachments);
        } catch (refreshError) {
          console.error('Error refreshing attachments:', refreshError);
        }
      }, 2000);
    }
  };
  
  // Handle file upload
  const handleFileUpload = async (file) => {
    try {
      setLoading(true);
      setError(null);
      
      // Check file size (max 95MB to be safe with 100MB server limit)
      const maxSizeMB = 95;
      const maxSizeBytes = maxSizeMB * 1024 * 1024;
      
      if (file.size > maxSizeBytes) {
        const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
        setError(`File is too large (${fileSizeMB}MB). Maximum size is ${maxSizeMB}MB.`);
        setLoading(false);
        return;
      }
      
      // Show upload progress message
      setSuccessMessage(`Uploading file (${(file.size / (1024 * 1024)).toFixed(2)}MB)...`);
      
      try {
        // Upload attachment
        const response = await uploadAttachment(sessionId, file);
        
        // Add the new attachment to the list
        setAttachments([...attachments, response]);
        
        // Show processing message
        setSuccessMessage('File uploaded successfully! Processing...');
        
        // Automatically process the file
        const processResponse = await processSingleAttachment(response.id);
        
        // Check if we're processing asynchronously
        if (processResponse.async && processResponse.task_id) {
          console.log(`Transcription started asynchronously with task ID: ${processResponse.task_id}`);
          
          // Add task to active tasks
          const newActiveTasks = { ...activeTasks };
          newActiveTasks[processResponse.task_id] = {
            task_id: processResponse.task_id,
            attachment_id: response.id,
            attachmentId: response.id, // For consistency with our data structure
            status: 'processing',
            progress: 0,
            complete: false,
            description: `Transcription of ${response.filename}`
          };
          
          setActiveTasks(newActiveTasks);
          
          // Start polling if not already polling
          if (!taskPollingInterval) {
            startTaskPolling();
          }
          
          // Show success message for async processing
          setSuccessMessage('File uploaded. Transcription started in background...');
        } else {
          // Synchronous processing completed
          // Reload attachments to get updated status
          const refreshedAttachments = await getAttachments(sessionId);
          setAttachments(refreshedAttachments);
          
          // Reload session to get updated answers
          const updatedSession = await getSession(sessionId);
          setSession(updatedSession);
          
          // Initialize answers from updated data
          const updatedAnswers = {};
          if (updatedSession.answers) {
            updatedSession.answers.forEach(answer => {
              updatedAnswers[answer.question_id] = answer.value;
            });
          }
          setAnswers(updatedAnswers);
          
          // Show final success message for sync processing
          setSuccessMessage('File processed successfully!');
        }
        
      } catch (uploadError) {
        console.error('Error uploading or processing file:', uploadError);
        
        // Handle specific error responses
        if (uploadError.response) {
          if (uploadError.response.status === 413) {
            // File too large error
            const errorData = uploadError.response.data;
            const maxSizeMB = errorData.max_size_mb || 100;
            const fileSizeMB = errorData.file_size_mb || (file.size / (1024 * 1024)).toFixed(2);
            
            setError(`File is too large (${fileSizeMB}MB). Maximum size is ${maxSizeMB}MB.`);
          } else if (uploadError.response.data && uploadError.response.data.message) {
            // Server returned a specific error message
            setError(`Upload failed: ${uploadError.response.data.message}`);
          } else {
            // Generic error with status code
            setError(`Upload failed with status ${uploadError.response.status}. Please try again.`);
          }
        } else if (uploadError.request) {
          // Request was made but no response received
          setError('No response from server. Please check your connection and try again.');
        } else {
          // Generic error
          setError(`Upload failed: ${uploadError.message}`);
        }
        throw uploadError; // Re-throw to be caught by outer catch
      }
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
      
    } catch (err) {
      console.error('Error in file upload process:', err);
      // Check if error message is already set
      if (!error) {
        setError('Failed to upload file. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // View transcription
  const handleViewTranscription = async (attachmentId) => {
    try {
      setError(null);
      
      // Get attachment details with transcription
      const attachment = await getAttachmentDetails(attachmentId, true);
      
      if (attachment.transcription) {
        setCurrentTranscription(attachment.transcription);
        setCurrentAttachmentId(attachmentId);
        setShowTranscriptionModal(true);
      } else {
        setError('No transcription available for this attachment. Please process the transcription first.');
        
        // Refresh attachments to ensure we have the latest data
        const refreshedAttachments = await getAttachments(sessionId);
        setAttachments(refreshedAttachments);
      }
    } catch (err) {
      console.error('Error fetching transcription:', err);
      setError('Failed to load transcription. Please try again.');
    }
  };

  // Fill answers from transcription
  const handleFillAnswers = async (attachmentId) => {
    try {
      setError(null);
      setSuccessMessage(null);
      
      // Show loading state
      const updatedAttachments = attachments.map(att => 
        att.id === attachmentId ? { ...att, status: 'processing' } : att
      );
      setAttachments(updatedAttachments);
      
      // First, check if attachment has a transcription
      const attachment = attachments.find(att => att.id === attachmentId);
      
      if (!attachment.transcription) {
        // Get attachment details with transcription to double check
        const attachmentDetails = await getAttachmentDetails(attachmentId, true);
        
        if (!attachmentDetails.transcription) {
          setError('No transcription available to fill answers from. Please process the transcription first.');
          
          // Revert attachment status
          const revertedAttachments = attachments.map(att => 
            att.id === attachmentId ? { ...att, status: 'done' } : att
          );
          setAttachments(revertedAttachments);
          return;
        }
      }
      
      // Use the new fillAnswersFromAttachment function instead of processSingleAttachment
      const result = await fillAnswersFromAttachment(attachmentId);
      
      // Reload session to get updated answers
      const updatedSession = await getSession(sessionId);
      setSession(updatedSession);
      
      // Initialize answers from updated data
      const updatedAnswers = {};
      if (updatedSession.answers) {
        updatedSession.answers.forEach(answer => {
          updatedAnswers[answer.question_id] = answer.value;
        });
      }
      setAnswers(updatedAnswers);
      
      // Show success message with count if available
      setSuccessMessage(`Successfully filled ${result.filled_answers || 0} answers from transcription`);
      
      // Reload attachments to get any status updates
      const refreshedAttachments = await getAttachments(sessionId);
      setAttachments(refreshedAttachments);
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
      
    } catch (err) {
      console.error('Error filling answers:', err);
      setError(`Failed to fill answers from transcription: ${err.message}`);
      
      // Revert attachment status
      const revertedAttachments = attachments.map(att => 
        att.id === attachmentId ? { ...att, status: 'done' } : att
      );
      setAttachments(revertedAttachments);
    }
  };

  // Export session as PDF
  const handleExportPDF = async () => {
    try {
      setExportingPdf(true);
      
      const apiUrl = process.env.REACT_APP_API_URL || 'https://deva-survey-api.promptcatalog.online';
      const response = await fetch(`${apiUrl}/sessions/${sessionId}/export/pdf`, {
        method: 'GET',
        headers: {
          'Accept': 'application/pdf',
        },
      });
      
      if (!response.ok) {
        throw new Error(`Export failed: ${response.status}`);
      }
      
      // Get filename from response headers or create default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'session_export.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      setSuccessMessage('PDF exported successfully!');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      setError(`Failed to export PDF: ${error.message}`);
    } finally {
      setExportingPdf(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        {error}
      </Alert>
    );
  }

  if (!session || !survey) {
    return (
      <Alert variant="warning">
        Session not found. It may have been deleted or the ID is incorrect.
      </Alert>
    );
  }


  // Get attachment status badge
  const getStatusBadge = (status) => {
    const variants = {
      'pending': 'warning',
      'processing': 'info',
      'done': 'success',
      'failed': 'danger'
    };
    
    return (
      <Badge bg={variants[status] || 'secondary'}>
        {status}
      </Badge>
    );
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1>{session.name || `Session ${session.id.slice(0, 8)}`}</h1>
          <h5 className="text-muted">
            <Link to={`/surveys/${survey.id}`}>{survey.title}</Link>
            {!backendOnline && (
              <Badge bg="danger" className="ms-2">Backend Offline</Badge>
            )}
          </h5>
        </div>
        <div>
          <Button as={Link} to={`/sessions/${sessionId}/realtime`} variant="outline-primary" className="me-2" disabled={!backendOnline}>
            Real-time Voice
          </Button>
          <Button 
            variant="outline-success" 
            className="me-2"
            onClick={handleExportPDF}
            disabled={exportingPdf || !backendOnline}
          >
            {exportingPdf ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Exporting...
              </>
            ) : (
              <>
                <i className="bi bi-download me-2"></i>
                Export PDF
              </>
            )}
          </Button>
          <Button as={Link} to={`/surveys/${survey.id}/sessions`} variant="outline-secondary">
            All Sessions
          </Button>
          {!backendOnline && (
            <Button 
              variant="warning" 
              size="sm" 
              className="ms-2"
              onClick={() => window.location.reload()}
            >
              Retry Connection
            </Button>
          )}
        </div>
      </div>
      
      {successMessage && (
        <Alert variant="success" dismissible onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}
      
      <Tab.Container defaultActiveKey="form">
        <Card className="mb-4">
          <Card.Header>
            <Nav variant="tabs">
              <Nav.Item>
                <Nav.Link eventKey="form">Survey Form</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="attachments">
                  Attachments ({attachments.length})
                </Nav.Link>
              </Nav.Item>
            </Nav>
          </Card.Header>
          
          <Card.Body>
            <Tab.Content>
              <Tab.Pane eventKey="form">
                <SurveyForm 
                  survey={survey}
                  answers={answers}
                  onAnswerChange={handleAnswerChange}
                  onSubmit={handleSubmit}
                  submitting={submitting}
                  showSubmitButton={true}
                  submitButtonText="Save Answers"
                  submitButtonVariant="primary"
                  submitButtonSize="lg"
                  recentlyFilledAnswers={[]}
                  emptyStateMessage="No answers filled yet. Start filling out the survey below."
                  emptyStateIcon={null}
                  cardClassName="mb-4"
                />
              </Tab.Pane>
              
              <Tab.Pane eventKey="attachments">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h3 className="h5 mb-0">Audio Attachments</h3>
                  <div>
                    <Button 
                      variant="primary" 
                      onClick={handleProcessAttachments}
                      disabled={processingAttachments || attachments.length === 0 || !attachments.some(a => a.status === 'pending')}
                      className="me-2"
                    >
                      {processingAttachments ? 'Processing...' : 'Process All Pending'}
                    </Button>
                    <Button 
                      as={Link} 
                      to={`/sessions/${sessionId}?upload=true`} 
                      variant="outline-primary"
                    >
                      Upload New
                    </Button>
                  </div>
                </div>
                
                {/* API Key Notice */}
                <Alert variant="info" className="mb-3">
                  <Alert.Heading>Custom Transcription Service</Alert.Heading>
                  <p>
                    This application uses a custom transcription service for audio processing.
                    The service is configured as follows:
                  </p>
                  <ul>
                    <li>Service URL: <code>https://aa73zge2v92mf2-8000.proxy.runpod.net/transcribe/</code></li>
                    <li>Configuration is managed in the <code>.env</code> file</li>
                  </ul>
                  <p className="mb-0">
                    <strong>Note:</strong> Audio files are processed through our custom transcription service instead of OpenAI Whisper.
                  </p>
                </Alert>
                
                {attachments.length === 0 ? (
                  <div className="text-center py-5">
                    <p className="mb-3">No attachments uploaded yet</p>
                    <p>Upload audio files to automatically fill survey answers</p>
                  </div>
                ) : (
                  <ListGroup>
                    {attachments.map(attachment => (
                      <ListGroup.Item 
                        key={attachment.id}
                        className={`d-flex justify-content-between align-items-center attachment-card ${attachment.status}`}
                      >
                        <div>
                          <div>
                            <span className="fw-bold">{attachment.filename}</span>
                          </div>
                          <div className="small text-muted">
                            Uploaded: {new Date(attachment.uploaded_at).toLocaleString()}
                          </div>
                          <div>
                            {getStatusBadge(attachment.status)}
                            {attachment.processed_at && (
                              <span className="small text-muted ms-2">
                                Processed: {new Date(attachment.processed_at).toLocaleString()}
                              </span>
                            )}
                          </div>
                        </div>
                        {attachment.status === 'pending' && (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleProcessAttachment(attachment.id)}
                          >
                            Process
                          </Button>
                        )}
                        {attachment.status === 'processing' && (
                          <div className="d-flex align-items-center">
                            <Spinner animation="border" size="sm" className="me-2" />
                            <Button
                              variant="outline-warning"
                              size="sm"
                              onClick={() => {
                                // Try to find any active tasks for this attachment
                                const attachmentTasks = Object.values(activeTasks).filter(
                                  task => task.attachment_id === attachment.id
                                );
                                
                                if (attachmentTasks.length > 0) {
                                  // If we have an active task, use its ID
                                  handleAbandonTask(attachmentTasks[0].task_id);
                                } else {
                                  // If no active task found, create a dummy to abandon in frontend only
                                  handleLocalAbandon(attachment.id);
                                }
                              }}
                            >
                              Abandon
                            </Button>
                          </div>
                        )}
                        {attachment.status === 'failed' && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleProcessAttachment(attachment.id)}
                          >
                            Retry
                          </Button>
                        )}
                        {attachment.status === 'done' && (
                          <div>
                            {/* Check if attachment has a transcription */}
                            {attachment.transcription ? (
                              <div>
                                <div className="mb-2">
                                  <span className="badge bg-success">Transcription Available</span>
                                </div>
                                <div className="d-flex">
                                  <Button
                                    variant="outline-info"
                                    size="sm"
                                    className="me-2"
                                    onClick={() => handleViewTranscription(attachment.id)}
                                  >
                                    View Transcription
                                  </Button>
                                  <Button
                                    variant="warning"
                                    size="sm"
                                    className="me-2"
                                    onClick={() => handleReprocessTranscription(attachment.id)}
                                  >
                                    Reprocess Transcription
                                  </Button>
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    className="me-2"
                                    onClick={() => handleRetryTranscription(attachment.id, true)}
                                  >
                                    Retry (Background)
                                  </Button>
                                  <Button
                                    variant="outline-success"
                                    size="sm"
                                    onClick={() => handleFillAnswers(attachment.id)}
                                  >
                                    Fill Answers
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <div>
                                <div className="mb-2">
                                  <span className="badge bg-secondary">No Transcription Yet</span>
                                </div>
                                <div className="d-flex flex-wrap">
                                  <Button
                                    variant="primary"
                                    size="sm"
                                    className="me-2 mb-1"
                                    onClick={() => handleReprocessTranscription(attachment.id)}
                                  >
                                    Process Transcription
                                  </Button>
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    className="mb-1"
                                    onClick={() => handleRetryTranscription(attachment.id)}
                                  >
                                    Process in Background
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {attachment.status === 'failed' && (
                          <div>
                            <div className="mb-2">
                              <span className="badge bg-danger">Transcription Failed</span>
                            </div>
                            <div className="d-flex flex-wrap">
                              <Button
                                variant="warning"
                                size="sm"
                                className="me-2 mb-1"
                                onClick={() => handleReprocessTranscription(attachment.id)}
                              >
                                Retry Transcription
                              </Button>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                className="mb-1"
                                onClick={() => handleRetryTranscription(attachment.id)}
                              >
                                Retry in Background
                              </Button>
                            </div>
                          </div>
                        )}
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                )}
                
                <div className="mt-4">
                  <Card>
                    <Card.Header>
                      <h4 className="h6 mb-0">Upload New Attachment</h4>
                    </Card.Header>
                    <Card.Body>
                      <Form onSubmit={(e) => {
                        e.preventDefault();
                        const fileInput = document.getElementById('audioFile');
                        if (fileInput.files.length > 0) {
                          const file = fileInput.files[0];
                          handleFileUpload(file);
                        } else {
                          setError('Please select a file to upload');
                        }
                      }}>
                        <Form.Group controlId="audioFile" className="mb-3">
                          <Form.Label>Select audio file</Form.Label>
                          <Form.Control 
                            type="file" 
                            accept=".mp3,.wav,.ogg,.m4a"
                          />
                          <Form.Text className="text-muted">
                            Supported formats: MP3, WAV, OGG, M4A
                          </Form.Text>
                        </Form.Group>
                        <Button 
                          type="submit" 
                          variant="primary"
                        >
                          Upload & Process
                        </Button>
                      </Form>
                    </Card.Body>
                  </Card>
                </div>
              </Tab.Pane>
            </Tab.Content>
          </Card.Body>
        </Card>
      </Tab.Container>

      {/* Transcription Modal */}
      <Modal 
        show={showTranscriptionModal} 
        onHide={() => setShowTranscriptionModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Audio Transcription</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <Card>
              <Card.Body>
                <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{currentTranscription}</p>
              </Card.Body>
            </Card>
          </div>
          <div className="d-flex justify-content-end">
            <Button 
              variant="outline-success" 
              className="me-2"
              onClick={() => {
                setShowTranscriptionModal(false);
                handleFillAnswers(currentAttachmentId);
              }}
            >
              Fill Answers with This Transcription
            </Button>
            <Button 
              variant="secondary" 
              onClick={() => setShowTranscriptionModal(false)}
            >
              Close
            </Button>
          </div>
        </Modal.Body>
      </Modal>
      
      {/* Cancel Task Confirmation Modal */}
      <Modal
        show={showCancelConfirmModal}
        onHide={() => setShowCancelConfirmModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Cancel Processing</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to cancel this processing task?</p>
          <p className="text-muted small">Note: The transcription service might continue running on the server, but the results will be discarded.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCancelConfirmModal(false)}>
            No, Continue Processing
          </Button>
          <Button variant="danger" onClick={confirmCancelTask}>
            Yes, Cancel Task
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Abandon Task Confirmation Modal */}
      <Modal
        show={showAbandonConfirmModal}
        onHide={() => setShowAbandonConfirmModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Abandon Stale Task</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>This task appears to be stale (no progress updates for a while).</p>
          <p>Are you sure you want to abandon this task?</p>
          <p className="text-muted small">
            Abandoning a task will mark it as failed but clean up resources associated with it. 
            You can create a new task for the same attachment afterward.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAbandonConfirmModal(false)}>
            No, Keep Task
          </Button>
          <Button variant="warning" onClick={confirmAbandonTask}>
            Yes, Abandon Task
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Active Tasks Display */}
      {Object.keys(activeTasks).length > 0 && (
        <Card className="mt-3 mb-4">
          <Card.Header>
            <h5 className="mb-0">Background Tasks ({Object.keys(activeTasks).length})</h5>
          </Card.Header>
          <ListGroup variant="flush">
            {Object.values(activeTasks).map(task => (
              <ListGroup.Item key={task.task_id}>
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <div>
                    <strong>{task.description || `Task for attachment ${task.attachment_id}`}</strong>
                    {task.is_stale && (
                      <Badge bg="warning" className="ms-2">Stale</Badge>
                    )}
                  </div>
                  <div>
                    {task.is_stale && (
                      <Button 
                        variant="outline-warning" 
                        size="sm"
                        className="me-2"
                        onClick={() => handleAbandonTask(task.task_id)}
                      >
                        Abandon
                      </Button>
                    )}
                    <Button 
                      variant="outline-danger" 
                      size="sm"
                      onClick={() => handleCancelTask(task.task_id)}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
                <div className="mb-2">
                  <ProgressBar 
                    now={task.progress || 0} 
                    label={`${task.progress || 0}%`} 
                    variant={task.is_stale ? "warning" : (task.progress > 90 ? "success" : "primary")}
                    animated={task.progress < 100 && !task.is_stale}
                  />
                </div>
                <div className="small text-muted">
                  <span>Status: {task.status}</span>
                  {task.start_time && (
                    <span className="ms-2">
                      Running for: {task.elapsed_formatted || `${Math.floor((Date.now()/1000 - task.start_time)/60)}m ${Math.floor((Date.now()/1000 - task.start_time) % 60)}s`}
                    </span>
                  )}
                  {task.is_stale && (
                    <span className="ms-2 text-warning">
                      No updates for {Math.floor(task.inactivity_time/60)}m {Math.floor(task.inactivity_time % 60)}s
                    </span>
                  )}
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </Card>
      )}
      
      {/* Stale Tasks Display */}
      {staleTasks.length > 0 && (
        <Card className="mt-3 mb-4 border-warning">
          <Card.Header className="bg-warning bg-opacity-25">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Stale Tasks ({staleTasks.length})</h5>
              <Button 
                variant="outline-primary" 
                size="sm"
                onClick={() => {
                  // Clear stale tasks list
                  setStaleTasks([]);
                }}
              >
                Dismiss All
              </Button>
            </div>
          </Card.Header>
          <ListGroup variant="flush">
            {staleTasks.map(task => (
              <ListGroup.Item key={task.task_id}>
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <div>
                    <strong>{task.description || `Task for attachment ${task.attachment_id}`}</strong>
                  </div>
                  <div className="btn-group">
                    <Button 
                      variant="warning" 
                      size="sm"
                      onClick={() => handleAbandonTask(task.task_id)}
                    >
                      Abandon
                    </Button>
                    <Button 
                      variant="outline-danger" 
                      size="sm"
                      onClick={() => handleCancelTask(task.task_id)}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <div className="small text-muted">
                    Inactive for {Math.floor(task.inactivity_time/60)}m {Math.floor(task.inactivity_time % 60)}s
                  </div>
                  {task.attachment_id && (
                    <Button 
                      variant="outline-primary" 
                      size="sm"
                      onClick={() => handleRetryTranscription(task.attachment_id)}
                    >
                      Retry Transcription
                    </Button>
                  )}
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </Card>
      )}
    </div>
  );
};

export default SessionDetail;
